import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";

import { SidebarProvider } from "@/components/ui/sidebar";
import { 
  Trash2, Plus, ArrowLeft, Calendar, 
  Edit, Clock, CheckCircle, Settings, 
  Users, BookOpen, MapPin, Save, List, Grid3X3, FileDown, AlertTriangle
} from "lucide-react";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { FaFolder } from "react-icons/fa";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format, addDays, eachDayOfInterval, isSameDay, parseISO } from "date-fns";
import { db, auth } from "@/firebase/config";
import { collection, addDoc, deleteDoc, doc, Timestamp, query, orderBy, setDoc, onSnapshot, serverTimestamp, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useSelector } from "react-redux";
import { selectUsers } from "@/State/usersSlice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AdminSidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { Eye } from "lucide-react";

// Types
interface Exam {
  id: string;
  subjectId: string;
  subjectName: string;
  classId: string;
  className: string;
  teacherId?: string; // Legacy support
  teacherName?: string; // Legacy support
  invigilators: string[]; // Array of teacher IDs
  invigilatorNames: string[]; // Array of teacher names
  roomId: string;
  roomName: string;
  date: any;
  startTime: string;
  endTime: string;
  notes?: string;
  streamInvigilators?: Record<string, string[]>; // Stream ID -> Array of Teacher IDs
  streamInvigilatorNames?: Record<string, string[]>;
}

interface TimeSlot {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  isBreak?: boolean;
}

interface Room {
  id: string;
  name: string;
  capacity: number;
}

interface ExamConfig {
  startDate: string;
  endDate: string;
  skipWeekends: boolean;
  published: boolean;
  useClassesAsRooms: boolean;
  examName: string;
  term: string;
  academicYear: string;
  lastEditedAt?: any;
  editedBy?: string;
  updatedAt?: any;
  slots?: TimeSlot[];
  mergeStreams: boolean;
}

interface SchoolClass {
  id: string;
  name: string;
  namingConvention?: string;
  level?: number;
}

interface Subject {
  id: string;
  name: string;
}

interface Teacher {
  id: string;
  name: string;
  employeeId?: string;
  regNumber?: string;
  teacherType: string[]; 
  title: string;
  firstName: string;
  lastName: string;
  tscNumber?: string;
}

// Helper to determine term
const getCurrentTerm = () => {
  const month = new Date().getMonth() + 1;
  if (month >= 1 && month <= 4) return "Term 1";
  if (month >= 5 && month <= 8) return "Term 2";
  return "Term 3";
};

// Default config
const DEFAULT_CONFIG: ExamConfig = {
  startDate: format(new Date(), "yyyy-MM-dd"),
  endDate: format(addDays(new Date(), 14), "yyyy-MM-dd"),
  skipWeekends: true,
  published: false,
  useClassesAsRooms: false,
  examName: "",
  term: getCurrentTerm(),
  academicYear: new Date().getFullYear().toString(),
  mergeStreams: true,
};

const DEFAULT_SLOTS: TimeSlot[] = [
  { id: "s1", name: "Morning Session", startTime: "08:00", endTime: "10:30" },
  { id: "s2", name: "Late Morning", startTime: "11:00", endTime: "13:00" },
  { id: "s3", name: "Afternoon Session", startTime: "14:00", endTime: "16:30" },
];

const DEFAULT_ROOMS: Room[] = [
  { id: "r1", name: "Hall A", capacity: 100 },
  { id: "r2", name: "Hall B", capacity: 80 },
  { id: "r3", name: "Room 101", capacity: 40 },
];

// Basic Error Boundary
class ErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: any}> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error("Uncaught error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900 rounded-md m-4">
          <h2 className="text-lg font-bold text-red-800 dark:text-red-400">Something went wrong</h2>
          <p className="text-sm text-red-600 dark:text-red-300 mt-2">{this.state.error?.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function ExamTimetable() {
  const { toast } = useToast();

  const usersState = useSelector(selectUsers);
  const schoolID = usersState.currentUser?.selectedAffiliation?.schoolID;

  // Mode and view
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [viewType, setViewType] = useState<"grid" | "list">("grid");
  
  // Data from Firebase
  const [exams, setExams] = useState<Exam[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(DEFAULT_SLOTS);
  const [rooms, setRooms] = useState<Room[]>(DEFAULT_ROOMS);
  const [config, setConfig] = useState<ExamConfig>(DEFAULT_CONFIG);
  
  
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Weekly View State
  const [viewStartDate, setViewStartDate] = useState<Date>(new Date());
  
  // Sync view date with config start date on load
  useEffect(() => {
      if (config.startDate) {
          setViewStartDate(parseISO(config.startDate));
      }
  }, [config.startDate]);
  
  // Filters
  const [filterClass, setFilterClass] = useState<string>("all");
  const [filterSubject, setFilterSubject] = useState<string>("all");
  const [filterStream, setFilterStream] = useState<string>("all"); // For filtering by individual stream within merged class
  
  // Dialogs
  // Dialogs
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [settingsTab, setSettingsTab] = useState("sessions");

  // Multi-timetable State
  const [selectedTimetableId, setSelectedTimetableId] = useState<string | null>(null);
  const [timetablesList, setTimetablesList] = useState<any[]>([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [headerSettings, setHeaderSettings] = useState<any>(null); // For PDF Header

  useEffect(() => {
    if (!schoolID) return;
    const fetchHeader = async () => {
      try {
        const docRef = doc(db, `schools/${schoolID}/headerSettings/default`);
        const snap = await getDoc(docRef);
        if (snap.exists()) setHeaderSettings(snap.data());
      } catch (e) { console.error("Err fetching header", e); }
    };
    fetchHeader();
  }, [schoolID]);

  // Helpers
  const getTimetablesCollection = () => schoolID ? `schools/${schoolID}/examTimetables` : `examTimetables`;
  const getCurrentTimetablePath = () => selectedTimetableId ? `${getTimetablesCollection()}/${selectedTimetableId}` : "";
  // Draft/Published separation: edit mode uses 'examsDraft', view mode uses 'examsPublished'
  const getDraftExamsPath = () => selectedTimetableId ? `${getCurrentTimetablePath()}/examsDraft` : "";
  const getPublishedExamsPath = () => selectedTimetableId ? `${getCurrentTimetablePath()}/examsPublished` : "";

  // Handlers
  const handleCreateNew = () => {
      setConfig({ ...DEFAULT_CONFIG, term: getCurrentTerm(), academicYear: new Date().getFullYear().toString() });
      setSelectedTimetableId(null);
      setShowNameDialog(true);
  };

  const handleSaveDetails = async () => {
      if (!schoolID) return;
      if (!selectedTimetableId) {
          // Create New
          const docRef = await addDoc(collection(db, getTimetablesCollection()), {
              ...config,
              createdAt: Timestamp.now(),
              slots: timeSlots // Save default slots
          });
          setSelectedTimetableId(docRef.id);
          setIsEditorOpen(true); // Open editor after creation
          toast({ title: "Timetable Created" });
      } else {
          // Update Existing
          await setDoc(doc(db, getCurrentTimetablePath()), { ...config, slots: timeSlots }, { merge: true });
          toast({ title: "Timetable Saved" });
          // Do not force open editor if we are just renaming in list view
      }
      setShowNameDialog(false);
  };

  const handleOpenTimetable = (id: string) => {
      setSelectedTimetableId(id);
      setIsEditorOpen(true);
  };

  const handleRenameTimetable = (t: any, e: any) => {
      e.stopPropagation();
      setSelectedTimetableId(t.id);
      setConfig({ ...DEFAULT_CONFIG, ...t }); // Load existing config
      setShowNameDialog(true);
  };

  const handleDeleteTimetable = async (id: string, e: any) => {
      e.stopPropagation();
      if (!confirm("Are you sure you want to delete this timetable?")) return;
      
      try {
          await deleteDoc(doc(db, getTimetablesCollection(), id));
          toast({ title: "Timetable Deleted" });
          if(selectedTimetableId === id) {
              setSelectedTimetableId(null);
              setIsEditorOpen(false);
          }
      } catch (error: any) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
      }
  };
  
  // Assignment form
  const [selectedSlot, setSelectedSlot] = useState<{ date: Date; timeSlot: TimeSlot } | null>(null);
  const [editingExamId, setEditingExamId] = useState<string | null>(null); // Track if editing existing exam
  const [assignForm, setAssignForm] = useState({
    subjectId: "",
    classId: "",
    invigilators: [] as string[],
    roomId: "",
    startTime: "",
    endTime: "",
    notes: "",
  });
  
  // New item forms
  const [newSlot, setNewSlot] = useState({ name: "", startTime: "", endTime: "" });
  const [newRoom, setNewRoom] = useState({ name: "", capacity: "" });
  const [streamInvigilators, setStreamInvigilators] = useState<Record<string, string[]>>({}); // { streamClassId: [teacherIds] }

  // Conflict State
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictDetails, setConflictDetails] = useState<{message: string, details: string[]} | null>(null);

  // Helper: Get streams for a merged class
  const getStreamsForMergedClass = (mergedClassId: string) => {
    if (!config.mergeStreams) return [];
    // We need to find all actual classes that belong to this group
    // The mergedClassId is the "name" we grouped by, e.g., "Form 1"
    
    return classes.filter(c => {
        // Logic must match effectiveClasses grouping logic
        if (c.namingConvention && c.level) {
            return `${c.namingConvention} ${c.level}` === mergedClassId;
        }
        // Fallback logic
        const parts = c.name.split(" ");
        let groupName = c.name;
        if (parts.length > 2) {
            groupName = parts.slice(0, -1).join(" ");
        }
        return groupName === mergedClassId;
    });
  };

  // Auto-fill Room based on Class + Config
  useEffect(() => {
    if (config.useClassesAsRooms && assignForm.classId) {
       setAssignForm(prev => ({ ...prev, roomId: assignForm.classId }));
    }
  }, [config.useClassesAsRooms, assignForm.classId]);

  // Paths - Use flat structure for documents (must have even segments)
  // Paths (Legacy/Global)
  const getGlobalConfigPath = () => schoolID ? `schools/${schoolID}/examTimetableConfig` : `examTimetableConfig`;
  const getSchoolPath = (sub: string) => schoolID ? `schools/${schoolID}/${sub}` : sub;

  // List Fetching
  useEffect(() => {
    if(!schoolID) return;
    const unsub = onSnapshot(query(collection(db, getTimetablesCollection()), orderBy("createdAt", "desc")), snap => {
        setTimetablesList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [schoolID]);

  // Generate visible dates (Weekly View)
  const visibleDates = useMemo(() => {
    // Generate 7 days starting from viewStartDate
    const dates = [];
    for (let i = 0; i < 7; i++) {
        dates.push(addDays(viewStartDate, i));
    }
    
    // Filter weekends if needed
    if (config.skipWeekends) {
      return dates.filter(d => d.getDay() !== 0 && d.getDay() !== 6);
    }
    return dates;
  }, [viewStartDate, config.skipWeekends]);

  // Filter exams
  const filteredExams = useMemo(() => {
    let result = exams;
    
    if (filterClass !== "all") {
      if (mode === "view" && config.mergeStreams) {
        // In View mode with mergeStreams: check if exam applies to this stream
        // An exam applies if:
        // 1. classId directly matches the filterClass (individual stream)
        // 2. OR the exam has streamInvigilators that include this stream
        // 3. OR the exam's classId is a merged class that contains this stream
        result = result.filter(e => {
          // Direct match
          if (e.classId === filterClass) return true;
          
          // Check if this exam has stream invigilators for this stream
          if (e.streamInvigilators && e.streamInvigilators[filterClass]) return true;
          
          // Check if the filterClass (stream) belongs to the exam's merged class
          // e.classId might be "Form 1" (merged), filterClass might be "Form 1 Blue" (stream)
          const selectedStream = classes.find(c => c.id === filterClass);
          if (selectedStream) {
            // Get the merged name for this stream
            let mergedName = selectedStream.name;
            if (selectedStream.namingConvention && selectedStream.level) {
              mergedName = `${selectedStream.namingConvention} ${selectedStream.level}`;
            } else {
              const parts = selectedStream.name.split(" ");
              if (parts.length > 2) {
                mergedName = parts.slice(0, -1).join(" ");
              }
            }
            // If the exam's classId matches the merged name, show the exam
            if (e.classId === mergedName) return true;
          }
          
          return false;
        });
      } else {
        // Edit mode or non-merged: direct match only
        result = result.filter(e => e.classId === filterClass);
      }
    }
    
    if (filterSubject !== "all") {
      result = result.filter(e => e.subjectId === filterSubject);
    }
    
    // Filter by stream (only in Edit mode with merged classes)
    if (filterStream !== "all" && config.mergeStreams && mode === "edit") {
      result = result.filter(e => {
        if (e.streamInvigilators && e.streamInvigilators[filterStream]) {
          return true;
        }
        return false;
      });
    }
    
    return result;
  }, [exams, filterClass, filterSubject, filterStream, config.mergeStreams, mode, classes]);

  // Helper: Merged Classes Logic
  const effectiveClasses = useMemo(() => {
    if (!config.mergeStreams) return classes;
    
    const groups: { [key: string]: SchoolClass } = {};
    
    classes.forEach(c => {
        let name = c.name;
        // logic from ClassRegistrationForm: name = `${namingConvention} ${level} ${stream}`
        // We want `${namingConvention} ${level}`
        if (c.namingConvention && c.level) {
            name = `${c.namingConvention} ${c.level}`;
        } else {
            // Fallback for classes without new metadata - simple heuristic
            // Try to split by space and take first 2 parts if > 2 parts
            const parts = c.name.split(" ");
            if (parts.length >= 2) {
                // Check if last part is likely a stream? 
                // Actually user said "form that is just a name... can be called grade"
                // If we don't have metadata, best effort:
                // If parts > 2, assume last is stream?
                // Or just don't merge if no metadata? 
                // Let's safe-guard: if parts > 2, take all except last
                if (parts.length > 2) { 
                   name = parts.slice(0, -1).join(" ");
                }
            }
        }
        
        // Ensure merged name is used as key
        if (!groups[name]) {
            groups[name] = { id: name, name: name }; 
        }
    });

    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [classes, config.mergeStreams]);

  // Get exam for cell
  const getExamForCell = (date: Date, slot: TimeSlot): Exam | null => {
    return filteredExams.find(e => {
      const examDate = e.date?.toDate ? e.date.toDate() : new Date(e.date);
      return isSameDay(examDate, date) && e.startTime === slot.startTime;
    }) || null;
  };

  // Conflict detection helper
  const isTeacherBusy = (teacherId: string, date: Date, startTime: string, excludeExamId?: string) => {
      const dateStr = format(date, "yyyy-MM-dd");
      const conflict = exams.find(e => {
          if (excludeExamId && e.id === excludeExamId) return false;
          // Date/Time Check
          const eDate = e.date?.toDate ? format(e.date.toDate(), "yyyy-MM-dd") : e.date;
          if (eDate !== dateStr || e.startTime !== startTime) return false;
          
          // Check if teacher is in this exam
          const displayInvigilators = e.invigilators || [];
          if (e.teacherId) displayInvigilators.push(e.teacherId);
          if (e.streamInvigilators) {
              Object.values(e.streamInvigilators).forEach(arr => {
                  if (Array.isArray(arr)) displayInvigilators.push(...arr);
              });
          }
          return displayInvigilators.includes(teacherId);
      });
      return conflict; // Returns the exam object if busy
  };

  // Conflict detection
  const getConflicts = (date: Date, startTime: string, form: typeof assignForm, currentStreamInvigilators: Record<string, string[]>, excludeId?: string): {message: string, details: string[]} | null => {
    const details: string[] = [];
    const dateStr = format(date, "yyyy-MM-dd");
    
    // Check for same teacher assigned to multiple streams within THIS assignment
    if (config.mergeStreams) {
      const teacherStreamMap: Record<string, string[]> = {}; // teacherId -> [streamNames]
      
      Object.entries(currentStreamInvigilators).forEach(([streamId, teacherIds]) => {
        const streamName = classes.find(c => c.id === streamId)?.name || streamId;
        teacherIds.forEach(tid => {
          if (!teacherStreamMap[tid]) teacherStreamMap[tid] = [];
          teacherStreamMap[tid].push(streamName);
        });
      });
      
      // Find teachers assigned to multiple streams
      Object.entries(teacherStreamMap).forEach(([tid, streams]) => {
        if (streams.length > 1) {
          const tName = teachers.find(t => t.id === tid)?.name || "Teacher";
          details.push(`${tName} is assigned to multiple streams: ${streams.join(", ")}`);
        }
      });
    }
    
    // Check venue and class conflicts first (hard blockers usually)
    exams.forEach(e => {
      if (excludeId && e.id === excludeId) return;
      const examDate = e.date?.toDate ? format(e.date.toDate(), "yyyy-MM-dd") : e.date;
      if (examDate !== dateStr || e.startTime !== startTime) return;
      
      if (form.roomId && e.roomId === form.roomId && form.roomId !== "auto") {
        const roomName = rooms.find(r => r.id === form.roomId)?.name || classes.find(c => c.id === form.roomId)?.name || "Room";
        details.push(`Venue "${roomName}" is already booked for ${e.subjectName}`);
      }
      if (form.classId && e.classId === form.classId) {
        details.push(`${classes.find(c => c.id === form.classId)?.name} already has an exam: ${e.subjectName}`);
      }
    });

    // Check all teachers against OTHER exams
    const teachersToCheck = new Set<string>([...form.invigilators]);
    Object.values(currentStreamInvigilators).forEach(ids => ids.forEach(id => teachersToCheck.add(id)));
    
    teachersToCheck.forEach(tid => {
        const busyExam = isTeacherBusy(tid, date, startTime, excludeId);
        if (busyExam) {
            const tName = teachers.find(t => t.id === tid)?.name || "Teacher";
            details.push(`${tName} is already invigilating ${busyExam.className} (${busyExam.subjectName})`);
        }
    });

    if (details.length > 0) {
        return { message: "There are scheduling conflicts", details };
    }
    return null;
  };

  // Fetch school data (classes, subjects, teachers)
  useEffect(() => {
    if (!schoolID) return;
    
    const unsubClasses = onSnapshot(collection(db, getSchoolPath("classes")), snap => {
      setClasses(snap.docs.map(d => ({ 
        id: d.id, 
        name: d.data().name,
        namingConvention: d.data().namingConvention,
        level: d.data().level
      })).sort((a, b) => a.name.localeCompare(b.name)));
    });
    
    const unsubSubjects = onSnapshot(collection(db, getSchoolPath("subjects")), snap => {
      setSubjects(snap.docs.map(d => ({ id: d.id, name: d.data().name })).sort((a, b) => a.name.localeCompare(b.name)));
    });
    const unsubTeachers = onSnapshot(collection(db, getSchoolPath("teachers")), snap => {
      setTeachers(snap.docs.map(d => {
        const data = d.data();
        const fullName = [data.title, data.firstName, data.lastName].filter(Boolean).join(" ");
        // Fallback to various ID fields if available
        const regNumber = data.employeeId || data.regNumber || data.idNumber || "";
        
        return { 
            id: d.id, 
            name: fullName, 
            employeeId: data.employeeId,
            regNumber,
            teacherType: data.teacherType || [], // e.g. ["Teacher", "Bursar"]
            title: data.title || "",
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            tscNumber: data.tscNumber || "" 
        };
      }).sort((a, b) => a.name.localeCompare(b.name)));
    });
    
    return () => { unsubClasses(); unsubSubjects(); unsubTeachers(); };
  }, [schoolID]);

  // Fetch exam config, slots, rooms
  // Fetch Single Timetable Data
  useEffect(() => {
    if (!schoolID || !selectedTimetableId) return;
    
    // Config & Slots (stored in main doc)
    const unsubConfig = onSnapshot(doc(db, getCurrentTimetablePath()), snap => {
        if (snap.exists()) {
            const data = snap.data();
            setConfig({ ...DEFAULT_CONFIG, ...data } as ExamConfig);
            if(data.slots) setTimeSlots(data.slots);
        }
    });

    // Rooms (Global)
    const roomsRef = doc(db, getGlobalConfigPath(), "rooms");
    const unsubRooms = onSnapshot(roomsRef, snap => {
      if (snap.exists() && snap.data().rooms) setRooms(snap.data().rooms);
    });

    // Exams (Subcollection) - Draft when editing, Published when viewing
    const examsPath = mode === "edit" ? getDraftExamsPath() : getPublishedExamsPath();
    if (!examsPath) return;
    const unsubExams = onSnapshot(query(collection(db, examsPath), orderBy("date")), snap => {
      setExams(snap.docs.map(d => ({ id: d.id, ...d.data() } as Exam)));
    }, (error) => {
      console.log("Exams collection empty or error:", error.message);
      setExams([]);
    });
    
    return () => { unsubConfig(); unsubRooms(); unsubExams(); };

  }, [schoolID, selectedTimetableId, mode]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, () => {
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);



  // Save slots
  // Save slots (Deprecated - handled in handleSaveDetails, but kept for standalone save)
  const saveSlots = async () => {
    if (!schoolID || !selectedTimetableId) return;
    await setDoc(doc(db, getCurrentTimetablePath()), { slots: timeSlots }, { merge: true });
    toast({ title: "Sessions saved" });
  };

  // Save rooms
  // Save rooms (Global)
  const saveRooms = async () => {
    if (!schoolID) return;
    await setDoc(doc(db, getGlobalConfigPath(), "rooms"), { rooms });
    toast({ title: "Rooms saved" });
  };

  // Save config
  const saveConfig = async () => {
    if (!schoolID || !selectedTimetableId) return;
    await setDoc(doc(db, getCurrentTimetablePath()), config, { merge: true });
    toast({ title: "Settings saved" });
  };

  // Add time slot
  const addTimeSlot = async () => {
    if (!newSlot.name || !newSlot.startTime || !newSlot.endTime) return;
    const slot: TimeSlot = { id: `s${Date.now()}`, ...newSlot };
    const updatedSlots = [...timeSlots, slot];
    setTimeSlots(updatedSlots);
    setNewSlot({ name: "", startTime: "", endTime: "" });
    
    if (schoolID && selectedTimetableId) {
        try {
            await setDoc(doc(db, getCurrentTimetablePath()), { slots: updatedSlots }, { merge: true });
            toast({ title: "Session added" });
        } catch(e: any) {
            console.error(e);
            toast({ title: "Error saving session", variant: "destructive" });
        }
    }
  };

  // Delete time slot
  const deleteTimeSlot = async (id: string) => {
    const updatedSlots = timeSlots.filter(s => s.id !== id);
    setTimeSlots(updatedSlots);
    
    if (schoolID && selectedTimetableId) {
        try {
            await setDoc(doc(db, getCurrentTimetablePath()), { slots: updatedSlots }, { merge: true });
            toast({ title: "Session deleted" });
        } catch(e: any) {
            console.error(e);
            toast({ title: "Error deleting session", variant: "destructive" });
        }
    }
  };

  // Add room
  const addRoom = () => {
    if (!newRoom.name || !newRoom.capacity) return;
    const room: Room = { id: `r${Date.now()}`, name: newRoom.name, capacity: parseInt(newRoom.capacity) };
    setRooms([...rooms, room]);
    setNewRoom({ name: "", capacity: "" });
  };

  // Delete room
  const deleteRoom = (id: string) => {
    setRooms(rooms.filter(r => r.id !== id));
  };

  // Handle cell click
  const handleCellClick = (date: Date, slot: TimeSlot) => {
    setSelectedSlot({ date, timeSlot: slot });
    
    // Check if there's an existing exam in this slot
    const existingExam = getExamForCell(date, slot);
    
    if (existingExam) {
      // Pre-fill form with existing exam data
      setEditingExamId(existingExam.id);
      setAssignForm({
        subjectId: existingExam.subjectId || "",
        classId: existingExam.classId || "",
        invigilators: existingExam.invigilators || [],
        roomId: existingExam.roomId || "",
        startTime: existingExam.startTime || slot.startTime,
        endTime: existingExam.endTime || slot.endTime,
        notes: existingExam.notes || "",
      });
      setStreamInvigilators(existingExam.streamInvigilators || {});
      setShowAssignDialog(true); // Open dialog to view details (read-only in view mode)
    } else if (mode === "edit") {
      // New exam - only allow in edit mode
      setEditingExamId(null);
      setAssignForm({
        subjectId: "",
        classId: filterClass !== "all" ? filterClass : "",
        invigilators: [],
        roomId: "",
        startTime: slot.startTime,
        endTime: slot.endTime,
        notes: "",
      });
      setStreamInvigilators({});
      setShowAssignDialog(true);
    }
    // If no exam and in view mode, do nothing (no empty slot clicking)
  };

  // Execute the actual save (called directly or after confirmation)
  const executeSaveExam = async () => {
      setLoading(true);
      try {
        if (!selectedTimetableId || !selectedSlot) return;
        
        const examData = {
          ...assignForm,
          roomId: config.mergeStreams ? "auto" : assignForm.roomId,
          subjectName: subjects.find(s => s.id === assignForm.subjectId)?.name || "",
          className: effectiveClasses.find(c => c.id === assignForm.classId)?.name || classes.find(c => c.id === assignForm.classId)?.name || "",
          invigilators: assignForm.invigilators,
          invigilatorNames: assignForm.invigilators.map(id => teachers.find(t => t.id === id)?.name || ""),
          roomName: config.mergeStreams 
              ? "Auto-selected" 
              : rooms.find(r => r.id === assignForm.roomId)?.name || classes.find(c => c.id === assignForm.roomId)?.name || "",
          streamInvigilators: config.mergeStreams ? streamInvigilators : {},
          streamInvigilatorNames: config.mergeStreams ? Object.entries(streamInvigilators).reduce((acc, [sid, tids]) => {
              acc[sid] = tids.map(tid => teachers.find(t => t.id === tid)?.name || "");
              return acc;
          }, {} as Record<string, string[]>) : {},
          date: Timestamp.fromDate(selectedSlot.date),
          updatedAt: Timestamp.now(),
        };
        
        if (editingExamId) {
          // Update existing exam
          await setDoc(doc(db, getDraftExamsPath(), editingExamId), examData, { merge: true });
          toast({ title: "Exam updated!" });
        } else {
          // Create new exam
          await addDoc(collection(db, getDraftExamsPath()), {
            ...examData,
            createdAt: Timestamp.now(),
          });
          toast({ title: "Exam scheduled!" });
        }
        
        setShowAssignDialog(false);
        setShowConflictDialog(false);
        setConflictDetails(null);
        setEditingExamId(null); // Reset editing state
      } catch (e: any) {
        toast({ title: "Error", description: e.message, variant: "destructive" });
      }
      setLoading(false);
  };

  // Assign exam handler
  const handleAssignExam = async () => {
    // If mergeStreams is on, roomId is not required (it's auto-selected)
    if (!selectedSlot || !assignForm.subjectId || !assignForm.classId || (!assignForm.roomId && !config.mergeStreams)) {
      toast({ title: "Fill all required fields", variant: "destructive" });
      return;
    }

    const conflictData = getConflicts(selectedSlot.date, assignForm.startTime, assignForm, streamInvigilators);
    if (conflictData) {
      setConflictDetails(conflictData);
      setShowConflictDialog(true);
      return;
    }

    await executeSaveExam();
  };

  // Delete exam (always from drafts)
  const handleDeleteExam = async (id: string) => {
    if (!confirm("Delete this exam?")) return;
    await deleteDoc(doc(db, getDraftExamsPath(), id));
    toast({ title: "Deleted" });
  };

  // Export PDF with Professional Matrix Design
  const handleExportPDF = async () => {
    if (filteredExams.length === 0) {
      toast({ title: "No exams to export", variant: "destructive" });
      return;
    }

    const doc = new jsPDF({ orientation: "landscape", format: "a4" });
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let currentY = 15;

    // Helper: Load Image safely
    const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = url;
            img.onload = () => resolve(img);
            img.onerror = (e) => reject(e);
        });
    };

    // --- Header Section ---
    const logoSize = 25; 
    let headerHeight = 40;

    // Draw Header
    if (headerSettings?.isEnabled) {
       // Logo (Left Aligned)
       if(headerSettings.logo?.url) {
           try {
               const img = await loadImage(headerSettings.logo.url);
               const ratio = img.height / img.width;
               doc.addImage(img, 'PNG', 15, 10, logoSize, logoSize * ratio);
           } catch (e) { console.warn("Logo load fail", e); }
       }

       // Text (Centered)
       const schoolName = headerSettings.schoolName?.text || "School Name";
       doc.setFont("helvetica", "bold");
       doc.setFontSize(18);
       doc.setTextColor(20, 20, 30);
       doc.text(schoolName.toUpperCase(), pageWidth / 2, 20, { align: "center" });

       const motto = headerSettings.motto?.text || "";
       if(motto) {
           doc.setFont("helvetica", "italic");
           doc.setFontSize(10);
           doc.setTextColor(80, 80, 80);
           doc.text(motto, pageWidth / 2, 26, { align: "center" });
       }
       
       const address = headerSettings.address?.text || "";
       if(address) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            const lines = doc.splitTextToSize(address, pageWidth - 100);
            doc.text(lines, pageWidth / 2, 32, { align: "center" });
       }
    } else {
        // Fallback Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.text((config.examName || "EXAM TIMETABLE").toUpperCase(), pageWidth / 2, 25, { align: "center" });
    }

    // Title & Context
    currentY = 45;
    doc.setDrawColor(200, 200, 200);
    doc.line(15, 40, pageWidth - 15, 40);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text((config.examName || "EXAM TIMETABLE").toUpperCase(), 15, currentY);

    const termText = `${config.term || "Term"} | ${config.academicYear || "Year"}`;
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text(termText, pageWidth - 15, currentY, { align: "right" });

    const classText = filterClass === "all" ? "ALL CLASSES" : (classes.find(c => c.id === filterClass)?.name || filterClass).toUpperCase();
    doc.text(`TIMETABLE FOR: ${classText}`, 15, currentY + 6);
    
    currentY += 12;

    // --- Matrix Data Construction ---
    // 1. Columns: Date + Time Slots
    const sortedSlots = [...timeSlots].sort((a,b) => a.startTime.localeCompare(b.startTime));
    
    const tableHead: any[] = [
        [
            { content: "DATE / DAY", styles: { halign: 'center', valign: 'middle', fillColor: [240, 240, 240], textColor: [20, 20, 20], fontStyle: 'bold' } },
            ...sortedSlots.map(s => ({
                content: `${s.name.toUpperCase()}\n${s.startTime} - ${s.endTime}`,
                styles: { halign: 'center', valign: 'middle', fillColor: [235, 242, 250], textColor: [30, 41, 59], fontStyle: 'bold' }
            }))
        ]
    ];

    // 2. Rows: Unique Dates -> Slots
    // Get all unique dates from exams AND config range? 
    // Better to use actual exam dates present in filtered set for compactness
    const uniqueDates = Array.from(new Set(filteredExams.map(e => {
        return e.date?.toDate ? format(e.date.toDate(), "yyyy-MM-dd") : e.date;
    }))).sort();

    const tableBody = uniqueDates.map(dateStr => {
        const rowData: any[] = [];
        const dateObj = parseISO(dateStr);
        
        // Col 1: Date
        rowData.push({
            content: format(dateObj, "EEEE\nMMM do, yyyy"),
            styles: { fontStyle: 'bold', halign: 'center', valign: 'middle', minCellWidth: 30 }
        });

        // Cols 2...N: Slots
        sortedSlots.forEach(slot => {
            // Find exams for this date & slot
            const slotExams = filteredExams.filter(e => {
                const eDate = e.date?.toDate ? format(e.date.toDate(), "yyyy-MM-dd") : e.date;
                return eDate === dateStr && e.startTime === slot.startTime;
            });

            if (slotExams.length === 0) {
                rowData.push(""); // Empty cell
            } else {
                // Determine content based on count
                // If distinct subjects/classes, list them
                const lines = slotExams.map(e => {
                     // Compact format: "Math (9A) - R1"
                     let line = `${e.subjectName}`;
                     if(filterClass === "all") line += ` (${e.className})`;
                     if(e.roomName && e.roomName !== "Auto-selected") line += `\n📍 ${e.roomName}`;
                     return line;
                });
                rowData.push(lines.join("\n\n"));
            }
        });
        
        return rowData;
    });

    // --- Draw AutoTable ---
    autoTable(doc, {
        startY: currentY,
        head: tableHead as any,
        body: tableBody as any,
        theme: 'grid',
        styles: {
            fontSize: 9,
            cellPadding: 4,
            lineColor: [200, 200, 200],
            lineWidth: 0.1,
            valign: 'middle',
            overflow: 'linebreak'
        },
        headStyles: {
            fillColor: [30, 41, 59],
            textColor: 255,
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [30, 41, 59]
        },
        columnStyles: {
            0: { cellWidth: 35, fontStyle: 'bold' } // Date Column fixed width
        },
        alternateRowStyles: {
            fillColor: [252, 252, 252]
        },
        margin: { left: 15, right: 15 },
        didDrawPage: (data) => {
             // Footer
             const footerY = pageHeight - 10;
             doc.setFontSize(8);
             doc.setTextColor(150);
             doc.text(`Generated by Vochak School System • ${format(new Date(), "PP p")}`, 15, footerY);
             const pageCount = (doc.internal as any).getNumberOfPages ? (doc.internal as any).getNumberOfPages() : 0;
             doc.text(`Page ${pageCount}`, pageWidth - 15, footerY, { align: "right" });
        }
    });

    const safeName = (config.examName || "timetable").replace(/[^a-z0-9]/gi, '_');
    doc.save(`${safeName}.pdf`);
    toast({ title: "PDF Exported (Landscape)" });
  };

  // Helper to auto-save config changes
  const handleConfigChange = async (key: keyof ExamConfig, value: any) => {
      const newConfig = { ...config, [key]: value };
      setConfig(newConfig);
      if (!schoolID || !selectedTimetableId) return;
      try {
          await setDoc(doc(db, getCurrentTimetablePath()), { [key]: value }, { merge: true });
      } catch (error) {
          console.error("Failed to save config:", error);
          toast({ title: "Failed to save setting", variant: "destructive" });
      }
  };

  // Navigate dates (Pagination)
  const goToPrevWeek = () => {
    setViewStartDate(prev => addDays(prev, -7));
  };
  const goToNextWeek = () => {
    setViewStartDate(prev => addDays(prev, 7));
  };

  // Save Progress Logic
  const handleSaveProgress = async () => {
    if (!schoolID || !selectedTimetableId) return;
    try {
      await setDoc(doc(db, getCurrentTimetablePath()), {
        updatedAt: serverTimestamp(),
        lastEditedAt: serverTimestamp(),
        editedBy: auth.currentUser?.email || auth.currentUser?.uid || 'Unknown'
      }, { merge: true });
      toast({ title: "Progress Saved", description: "Your changes have been saved." });
    } catch (error: any) {
      console.error("Save progress error:", error);
      toast({ title: "Error Saving", description: error.message, variant: "destructive" });
    }
  };

  // Publish Logic - Copies drafts to published
  const handlePublish = async () => {
    if (!schoolID || !selectedTimetableId) return;
    if (!confirm("Are you sure you want to publish this timetable? It will be visible to all users.")) return;
    try {
      // 1. Update timetable metadata
      await setDoc(doc(db, getCurrentTimetablePath()), {
        published: true,
        publishedAt: serverTimestamp(),
        publishedBy: auth.currentUser?.uid,
        status: "published"
      }, { merge: true });

      // 2. Copy all draft exams to published collection
      const draftPath = getDraftExamsPath();
      const publishedPath = getPublishedExamsPath();
      
      // First, clear existing published exams (to handle deletions)
      const existingPublished = await import("firebase/firestore").then(m => m.getDocs(collection(db, publishedPath)));
      const deletePromises = existingPublished.docs.map(d => deleteDoc(doc(db, publishedPath, d.id)));
      await Promise.all(deletePromises);
      
      // Then copy all drafts to published
      const drafts = await import("firebase/firestore").then(m => m.getDocs(collection(db, draftPath)));
      const copyPromises = drafts.docs.map(d => setDoc(doc(db, publishedPath, d.id), d.data()));
      await Promise.all(copyPromises);

      setConfig(prev => ({ ...prev, published: true }));
      toast({ title: "Timetable Published", description: `${drafts.docs.length} exams are now visible to all users.` });
    } catch (error: any) {
      console.error("Publish error:", error);
      toast({ title: "Error Publishing", description: error.message, variant: "destructive" });
    }
  };

  return (
    <SidebarProvider>
      <ErrorBoundary>
      <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
        <div className="sticky top-0 h-screen hidden md:block">
          <AdminSidebar onSectionChange={() => {}} />
        </div>
        <div className="flex flex-1 flex-col overflow-hidden">
          <Header />
          <main className="flex-1 p-3 md:p-6 overflow-y-auto">
            {isLoading ? (
              <div className="flex h-full items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
            <>
            {!isEditorOpen ? (
                // LIST VIEW
                <div className="max-w-7xl mx-auto space-y-6">
                    <div className="flex justify-between items-center bg-white dark:bg-[#020817] p-6 rounded-xl border border-gray-200 dark:border-[#1E293B] shadow-sm">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Exam Timetables</h1>
                            <p className="text-gray-500 dark:text-gray-400">Manage all your school's exam schedules in one place.</p>
                        </div>
                        <Button onClick={handleCreateNew} size="lg" className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white shadow-lg shadow-blue-500/20">
                            <Plus className="mr-2 h-5 w-5" /> Create New Timetable
                        </Button>
                    </div>

                    {/* Folders Grid Map */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-8">
                        
                        {/* Render Timetables as Folders */}
                        {timetablesList.map(t => (
                            <div 
                                key={t.id} 
                                onClick={() => handleOpenTimetable(t.id)}
                                className="group relative flex flex-col items-center justify-start cursor-pointer transition-all duration-300"
                            >
                                {/* Context Menu Button - Always Visible, Moved to Corner */}
                                <div className="absolute top-0 right-2 z-20">
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-7 w-7 text-gray-500 hover:text-gray-700 bg-white/80 hover:bg-white shadow-sm rounded-full border border-gray-200 dark:bg-black/40 dark:text-slate-200 dark:border-gray-700 dark:hover:bg-black/60 dark:hover:text-white transition-all backdrop-blur-sm" 
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <BiDotsVerticalRounded className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-40 bg-white dark:bg-[#0F172A] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-white">
                                            <DropdownMenuItem onClick={(e) => handleRenameTimetable(t, e)} className="cursor-pointer hover:bg-gray-100 dark:hover:bg-[#1E293B] focus:bg-gray-100 dark:focus:bg-[#1E293B]">
                                                <Edit className="mr-2 h-4 w-4" /> Rename
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => handleDeleteTimetable(t.id, e)} className="cursor-pointer text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 focus:bg-red-50 dark:focus:bg-red-900/10 hover:text-red-700 dark:hover:text-red-400">
                                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                {/* Folder Icon */}
                                <div className="relative mb-3 transition-transform duration-300 group-hover:scale-105 group-hover:-translate-y-2 filter group-hover:drop-shadow-xl">
                                    {/* SVG Defs for Blending Blue Gradient */}
                                    <svg width="0" height="0">
                                      <linearGradient id="blueFolderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                        <stop stopColor="#3B82F6" offset="0%" />
                                        <stop stopColor="#2563EB" offset="100%" />
                                      </linearGradient>
                                    </svg>
                                    
                                    <FaFolder size={110} style={{ fill: "url(#blueFolderGradient)" }} className="drop-shadow-md" />
                                    
                                    {/* Term Badge */}
                                    {t.term && (
                                        <div className="absolute top-10 left-1/2 -translate-x-1/2 bg-white/95 text-[#3B82F6] text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-md border border-blue-100 whitespace-nowrap tracking-wide z-10">
                                            {t.term.toUpperCase()}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Info / Name */}
                                <div className="w-full text-center space-y-1.5 px-2 py-2 rounded-xl group-hover:bg-gray-100 dark:group-hover:bg-white/5 transition-colors duration-200">
                                    <h3 className="text-gray-900 dark:text-white font-semibold text-sm tracking-tight truncate w-full group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" title={t.examName}>
                                        {t.examName || "Untitled Timetable"}
                                    </h3>
                                    <div className="flex flex-col items-center justify-center gap-1 text-[11px] font-medium text-gray-500 dark:text-slate-400 group-hover:text-gray-700 dark:group-hover:text-slate-300 transition-colors">
                                        <span>{t.academicYear}</span>
                                        {t.lastEditedAt && (
                                            <div className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-slate-500">
                                                <Clock className="w-3 h-3" />
                                                <span>
                                                    {t.lastEditedAt?.toDate ? format(t.lastEditedAt.toDate(), "MMM d, h:mm a") : "Just now"}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {/* Empty State */}
                        {timetablesList.length === 0 && (
                            <div className="col-span-full flex flex-col items-center justify-center py-20 border-2 border-dashed border-[#1E293B] rounded-xl bg-[#020817]/50 mt-4">
                                <div className="w-20 h-20 bg-[#1E293B] rounded-full flex items-center justify-center mb-4 ring-4 ring-[#1E293B]/50">
                                    <FaFolder className="h-10 w-10 text-gray-500 opacity-50 grayscale" />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2 tracking-tight">No Timetables Found</h3>
                                <p className="text-sm text-slate-400 mb-8 max-w-sm text-center leading-relaxed">
                                    Your school currently has no exam schedules. Create your first timetable to get started.
                                </p>
                                <Button onClick={handleCreateNew} size="lg" className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white font-semibold shadow-lg shadow-blue-500/20 transition-all hover:scale-105 active:scale-95">
                                    <Plus className="mr-2 h-5 w-5" /> Create Timetable
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                // EDITOR VIEW
                <div className="max-w-7xl mx-auto space-y-4">
              
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Button variant="ghost" size="icon" onClick={() => setIsEditorOpen(false)}>
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex-1 max-w-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 rounded p-2 transition-colors" onClick={() => setShowNameDialog(true)}>
                    <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                       {config.term && <span className="text-muted-foreground font-medium">{config.term}:</span>}
                       {config.examName || "Exam Timetable"}
                       <Edit className="h-4 w-4 text-muted-foreground opacity-50" />
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {mode === "view" ? "View exam schedules" : "Click cells to schedule exams"}
                    </p>
                    {config.lastEditedAt && (
                       <Badge variant="outline" className="hidden sm:flex items-center gap-1 text-xs bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 mt-1 w-fit">
                          <Clock className="w-3 h-3" />
                          <span>
                            Last edited: {config.lastEditedAt?.toDate ? format(config.lastEditedAt.toDate(), "MMM d, h:mm a") : "Recently"}
                            {config.editedBy && <span className="ml-1 opacity-75">by {config.editedBy}</span>}
                          </span>
                       </Badge>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">

                  {mode === "edit" && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setShowSettingsDialog(true)} className="border-[#E2E8F0] dark:border-[#1E293B]">
                        <Settings className="h-4 w-4 mr-1" />Settings
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleSaveProgress} className="border-[#E2E8F0] dark:border-[#1E293B]">
                        <Save className="h-4 w-4 mr-1" />Save Progress
                      </Button>
                      <Button size="sm" onClick={handlePublish} className="bg-green-600 hover:bg-green-700 text-white">
                        <CheckCircle className="h-4 w-4 mr-1" />Publish
                      </Button>
                    </>
                  )}
                  <Button size="sm" onClick={handleExportPDF} className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white">
                    <FileDown className="h-4 w-4 mr-1" />PDF
                  </Button>
                </div>
              </div>

              {/* Controls */}
              <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center">
                    {/* Mode Toggle */}
                    <div className="flex rounded-lg border border-[#E2E8F0] dark:border-[#1E293B] p-1 bg-[#F1F5F9] dark:bg-[#0F172A]">
                      <Button variant={mode === "view" ? "default" : "ghost"} size="sm" onClick={() => setMode("view")}
                        className={mode === "view" ? "bg-blue-600 text-white" : ""}>
                        <Eye className="w-4 h-4 mr-1" />View
                      </Button>
                      <Button variant={mode === "edit" ? "default" : "ghost"} size="sm" onClick={() => setMode("edit")}
                        className={mode === "edit" ? "bg-blue-600 text-white" : ""}>
                        <Edit className="w-4 h-4 mr-1" />Edit
                      </Button>
                    </div>

                    {/* View Type Toggle */}
                    <div className="flex rounded-lg border border-[#E2E8F0] dark:border-[#1E293B] p-1 bg-[#F1F5F9] dark:bg-[#0F172A]">
                      <Button variant={viewType === "grid" ? "default" : "ghost"} size="sm" onClick={() => setViewType("grid")}
                        className={viewType === "grid" ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-black" : ""}>
                        <Grid3X3 className="w-4 h-4" />
                      </Button>
                      <Button variant={viewType === "list" ? "default" : "ghost"} size="sm" onClick={() => setViewType("list")}
                        className={viewType === "list" ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-black" : ""}>
                        <List className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={filterClass} onValueChange={(v) => { setFilterClass(v); setFilterStream("all"); }}>
                        <SelectTrigger className="w-[160px] h-9 border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#0F172A]">
                          <SelectValue placeholder="All Classes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Classes</SelectItem>
                          {/* In View mode: show all individual streams. In Edit mode: show merged classes */}
                          {(mode === "view" ? classes : effectiveClasses).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      
                      {/* Stream Filter - Only in Edit mode when mergeStreams is on and a merged class is selected */}
                      {mode === "edit" && config.mergeStreams && filterClass !== "all" && getStreamsForMergedClass(filterClass).length > 0 && (
                        <Select value={filterStream} onValueChange={setFilterStream}>
                          <SelectTrigger className="w-[150px] h-9 border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#0F172A]">
                            <SelectValue placeholder="All Streams" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Streams</SelectItem>
                            {getStreamsForMergedClass(filterClass).map(s => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      <Select value={filterSubject} onValueChange={setFilterSubject}>
                        <SelectTrigger className="w-[140px] h-9 border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#0F172A]">
                          <SelectValue placeholder="All Subjects" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Subjects</SelectItem>
                          {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Date Navigation */}
                    <div className="flex items-center gap-2 lg:ml-auto">
                      <Button variant="outline" size="sm" onClick={goToPrevWeek} className="border-[#E2E8F0] dark:border-[#1E293B]">←</Button>
                      <span className="text-sm font-medium whitespace-nowrap">
                        {visibleDates.length > 0 && `${format(visibleDates[0], "MMM d")} - ${format(visibleDates[visibleDates.length - 1], "MMM d")}`}
                      </span>
                      <Button variant="outline" size="sm" onClick={goToNextWeek} className="border-[#E2E8F0] dark:border-[#1E293B]">→</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Unpublished Warning */}
              {mode === "view" && !config.published && (
                <Card className="border border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20">
                  <CardContent className="p-4 flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">This timetable has not been published yet</p>
                      <p className="text-xs text-amber-600 dark:text-amber-400">Switch to Edit mode to add exams, then click Publish to make them visible.</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card className="p-3 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold">{filteredExams.length}</p>
                      <p className="text-xs text-muted-foreground">Exams</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-2xl font-bold">{classes.length}</p>
                      <p className="text-xs text-muted-foreground">Classes</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="text-2xl font-bold">{subjects.length}</p>
                      <p className="text-xs text-muted-foreground">Subjects</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-orange-500" />
                    <div>
                      <p className="text-2xl font-bold">{rooms.length}</p>
                      <p className="text-xs text-muted-foreground">Rooms</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Grid View */}
              {viewType === "grid" && (
                <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      {filterClass === "all" ? "All Exams" : classes.find(c => c.id === filterClass)?.name}
                      <Badge variant="secondary">{filteredExams.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-2 sm:p-4 overflow-x-auto">
                    <div className="min-w-[700px]">
                      {/* Header */}
                      <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: `90px repeat(${visibleDates.length}, 1fr)` }}>
                        <div className="p-2 bg-[#F1F5F9] dark:bg-[#0F172A] rounded text-center text-xs font-medium">Time</div>
                        {visibleDates.map(date => (
                          <div key={date.toISOString()} className="p-2 bg-[#F1F5F9] dark:bg-[#0F172A] rounded text-center text-xs">
                            <div className="font-medium">{format(date, "EEE")}</div>
                            <div className="text-muted-foreground">{format(date, "MMM d")}</div>
                          </div>
                        ))}
                      </div>

                      {/* Rows */}
                      {timeSlots.filter(s => !s.isBreak).map(slot => (
                        <div key={slot.id} className="grid gap-1 mb-1" style={{ gridTemplateColumns: `90px repeat(${visibleDates.length}, 1fr)` }}>
                          <div className="p-2 bg-[#F1F5F9] dark:bg-[#0F172A] rounded text-xs">
                            <div className="font-medium">{slot.name}</div>
                            <div className="text-muted-foreground">{slot.startTime}-{slot.endTime}</div>
                          </div>
                          {visibleDates.map(date => {
                            const exam = getExamForCell(date, slot);
                            return (
                              <div
                                key={`${date.toISOString()}-${slot.id}`}
                                onClick={() => handleCellClick(date, slot)}
                                className={`
                                  min-h-[70px] p-2 rounded border transition-all text-xs
                                  ${mode === "edit" ? "cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950" : ""}
                                  ${exam ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700" : "border-[#E2E8F0] dark:border-[#1E293B]"}
                                `}
                              >
                                {exam ? (
                                  <>
                                    <div className="font-semibold text-blue-700 dark:text-blue-300">{exam.subjectName}</div>
                                    <Badge variant="outline" className="text-[10px] mt-1">{exam.className}</Badge>
                                    <div className="text-muted-foreground mt-1">{exam.roomName}</div>
                                    {mode === "edit" && (
                                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 mt-1 text-red-500"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteExam(exam.id); }}>
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </>
                                ) : mode === "edit" && (
                                  <div className="h-full flex items-center justify-center text-muted-foreground">
                                    <Plus className="h-4 w-4" />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* List View */}
              {viewType === "list" && (
                <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Exam List</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Time</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Class</TableHead>
                          <TableHead>Room</TableHead>
                          <TableHead>Invigilator</TableHead>
                          {mode === "edit" && <TableHead></TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredExams.length === 0 ? (
                          <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No exams scheduled</TableCell></TableRow>
                        ) : filteredExams.map(e => (
                          <TableRow key={e.id}>
                            <TableCell>{e.date?.toDate ? format(e.date.toDate(), "EEE, MMM d") : e.date}</TableCell>
                            <TableCell>{e.startTime} - {e.endTime}</TableCell>
                            <TableCell className="font-medium">{e.subjectName}</TableCell>
                            <TableCell><Badge variant="outline">{e.className}</Badge></TableCell>
                            <TableCell>{e.roomName}</TableCell>
                            <TableCell>{(e.invigilatorNames && e.invigilatorNames.length > 0) ? e.invigilatorNames.join(", ") : (e.teacherName || "-")}</TableCell>
                            {mode === "edit" && (
                              <TableCell>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteExam(e.id)} className="text-red-500">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
         </div>
       )}
      </>
      )}
      </main>
        </div>
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto border border-[#1E293B] bg-[#020817] text-white">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Exam Timetable Settings</DialogTitle>
          </DialogHeader>
          <Tabs value={settingsTab} onValueChange={setSettingsTab}>
            <TabsList className="grid grid-cols-3 mb-6 bg-[#0F172A] border border-[#1E293B] p-1">
              <TabsTrigger value="sessions" className="data-[state=active]:bg-[#3879E3] data-[state=active]:text-white">Sessions</TabsTrigger>
              <TabsTrigger value="rooms" className="data-[state=active]:bg-[#3879E3] data-[state=active]:text-white">Rooms</TabsTrigger>
              <TabsTrigger value="dates" className="data-[state=active]:bg-[#3879E3] data-[state=active]:text-white">General</TabsTrigger>
            </TabsList>

            {/* Sessions Tab */}
            <TabsContent value="sessions" className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Input 
                  placeholder="Session name" 
                  value={newSlot.name} 
                  onChange={e => setNewSlot({...newSlot, name: e.target.value})} 
                  className="border-[#1E293B] bg-[#0F172A] text-white placeholder:text-gray-500"
                />
                <Input 
                  type="time" 
                  value={newSlot.startTime} 
                  onChange={e => setNewSlot({...newSlot, startTime: e.target.value})} 
                  className="border-[#1E293B] bg-[#0F172A] text-white"
                />
                <Input 
                  type="time" 
                  value={newSlot.endTime} 
                  onChange={e => setNewSlot({...newSlot, endTime: e.target.value})} 
                  className="border-[#1E293B] bg-[#0F172A] text-white"
                />
              </div>
              <Button onClick={addTimeSlot} size="sm" className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white mt-2">
                <Plus className="h-4 w-4 mr-1" />Add Session
              </Button>
              <div className="space-y-3 mt-4">
                {timeSlots.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-4 border border-[#1E293B] rounded-lg bg-[#0F172A] m-2">
                    <div>
                      <p className="font-medium text-white">{s.name}</p>
                      <p className="text-sm text-gray-400">{s.startTime} - {s.endTime}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteTimeSlot(s.id)} className="text-gray-400 hover:text-red-500 hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button onClick={saveSlots} className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white mt-4">
                <Save className="h-4 w-4 mr-1" />Save Sessions
              </Button>
            </TabsContent>

            {/* Rooms Tab */}
            <TabsContent value="rooms" className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Input 
                  placeholder="Room name" 
                  value={newRoom.name} 
                  onChange={e => setNewRoom({...newRoom, name: e.target.value})} 
                  className="border-[#1E293B] bg-[#0F172A] text-white placeholder:text-gray-500"
                />
                <Input 
                  type="number" 
                  placeholder="Capacity" 
                  value={newRoom.capacity} 
                  onChange={e => setNewRoom({...newRoom, capacity: e.target.value})} 
                  className="border-[#1E293B] bg-[#0F172A] text-white placeholder:text-gray-500"
                />
              </div>
              <Button onClick={addRoom} size="sm" className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white mt-2">
                <Plus className="h-4 w-4 mr-1" />Add Room
              </Button>
              <div className="space-y-3 mt-4">
                {rooms.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-4 border border-[#1E293B] rounded-lg bg-[#0F172A] m-2">
                    <div>
                      <p className="font-medium text-white">{r.name}</p>
                      <p className="text-sm text-gray-400">Capacity: {r.capacity}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => deleteRoom(r.id)} className="text-gray-400 hover:text-red-500 hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 p-4 border border-[#1E293B] rounded-lg bg-[#0F172A] mb-4">
                <Switch 
                  checked={config.useClassesAsRooms} 
                  onCheckedChange={v => handleConfigChange('useClassesAsRooms', v)} 
                />
                <div>
                  <Label className="text-white">Use classes as rooms</Label>
                  <p className="text-xs text-gray-400">Allows selecting school classes as exam venues</p>
                </div>
              </div>
              <Button onClick={saveRooms} className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white mt-4">
                <Save className="h-4 w-4 mr-1" />Save Rooms
              </Button>
            </TabsContent>

            {/* Dates Tab (General Settings) */}
            <TabsContent value="dates" className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Exam Name</Label>
                  <Input 
                    placeholder="e.g. Mid-Year Assessment" 
                    value={config.examName} 
                    onChange={e => setConfig({...config, examName: e.target.value})} 
                    className="border-[#1E293B] bg-[#0F172A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">Term</Label>
                  <Select 
                    value={config.term} 
                    onValueChange={v => setConfig({...config, term: v})}
                  >
                    <SelectTrigger className="border-[#1E293B] bg-[#0F172A] text-white">
                      <SelectValue placeholder="Select Term" />
                    </SelectTrigger>
                    <SelectContent className="border-[#1E293B] bg-[#0F172A]">
                      <SelectItem value="Term 1" className="text-white hover:bg-[#1E293B]">Term 1</SelectItem>
                      <SelectItem value="Term 2" className="text-white hover:bg-[#1E293B]">Term 2</SelectItem>
                      <SelectItem value="Term 3" className="text-white hover:bg-[#1E293B]">Term 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Start Date</Label>
                  <Input 
                    type="date" 
                    value={config.startDate} 
                    onChange={e => setConfig({...config, startDate: e.target.value})} 
                    className="border-[#1E293B] bg-[#0F172A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-300">End Date</Label>
                  <Input 
                    type="date" 
                    value={config.endDate} 
                    onChange={e => setConfig({...config, endDate: e.target.value})} 
                    className="border-[#1E293B] bg-[#0F172A] text-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 border border-[#1E293B] rounded-lg bg-[#0F172A] mt-2">
                <Switch checked={config.skipWeekends} onCheckedChange={v => handleConfigChange('skipWeekends', v)} />
                <Label className="text-white">Skip weekends</Label>
              </div>
              <div className="flex items-center gap-3 p-4 border border-[#1E293B] rounded-lg bg-[#0F172A] mt-2">
                <Switch checked={config.mergeStreams} onCheckedChange={v => handleConfigChange('mergeStreams', v)} />
                <div>
                  <Label className="text-white">Merge Streams</Label>
                  <p className="text-xs text-gray-400">Combine streams (e.g. "Form 1 Blue") into single forms ("Form 1")</p>
                </div>
              </div>
              <Button onClick={saveConfig} className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white mt-2">
                <Save className="h-4 w-4 mr-1" />Save Settings
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="border border-[#1E293B] bg-[#020817] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">
              {mode === "view" ? "Exam Details" : editingExamId ? "Edit Exam" : "Schedule Exam"}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedSlot && <>{format(selectedSlot.date, "EEEE, MMM d")} • {selectedSlot.timeSlot.startTime} - {selectedSlot.timeSlot.endTime}</>}
              {mode === "view" && <span className="ml-2 text-xs bg-gray-700 px-2 py-0.5 rounded">Read-only</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Subject *</Label>
              <Select value={assignForm.subjectId} onValueChange={v => setAssignForm({...assignForm, subjectId: v})}>
                <SelectTrigger className="border-[#1E293B] bg-[#0F172A] text-white">
                  <SelectValue placeholder="Select subject" />
                </SelectTrigger>
                <SelectContent className="border-[#1E293B] bg-[#0F172A]">
                  {subjects.map(s => <SelectItem key={s.id} value={s.id} className="text-white hover:bg-[#1E293B]">{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Class *</Label>
              <Select value={assignForm.classId} onValueChange={v => setAssignForm({...assignForm, classId: v})}>
                <SelectTrigger className="border-[#1E293B] bg-[#0F172A] text-white">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent className="border-[#1E293B] bg-[#0F172A]">
                  {effectiveClasses.map(c => <SelectItem key={c.id} value={c.id} className="text-white hover:bg-[#1E293B]">{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Room *</Label>
              {config.mergeStreams ? (
                 <div className="flex h-10 w-full items-center justify-between rounded-md border border-[#1E293B] bg-[#1E293B]/50 px-3 py-2 text-sm text-gray-400 italic">
                    Auto-selected for each stream level
                 </div>
              ) : (
              <Select value={assignForm.roomId} onValueChange={v => setAssignForm({...assignForm, roomId: v})}>
                <SelectTrigger className="border-[#1E293B] bg-[#0F172A] text-white">
                  <SelectValue placeholder="Select room" />
                </SelectTrigger>
                <SelectContent className="border-[#1E293B] bg-[#0F172A]">
                  {!config.useClassesAsRooms && (
                    <>
                       <div className="px-2 py-1 text-[10px] uppercase text-gray-500 font-bold">Standard Rooms</div>
                       {rooms.map(r => <SelectItem key={r.id} value={r.id} className="text-white hover:bg-[#1E293B]">{r.name} ({r.capacity})</SelectItem>)}
                    </>
                  )}
                  {config.useClassesAsRooms && (
                    <>
                      <div className="px-2 py-1 text-[10px] uppercase text-gray-500 font-bold border-t border-[#1E293B] mt-1">School Classes</div>
                      {classes.map(c => <SelectItem key={c.id} value={c.id} className="text-white hover:bg-[#1E293B]">{c.name} (Classroom)</SelectItem>)}
                    </>
                  )}
                </SelectContent>
              </Select>
              )}
            </div>

            
            {/* Invigilator Selection */}
            {config.mergeStreams && assignForm.classId && getStreamsForMergedClass(assignForm.classId).length > 0 ? (
               <div className="space-y-3 border border-[#1E293B] bg-[#0F172A]/50 p-3 rounded-md">
                   <div className="flex items-center justify-between">
                     <Label className="text-gray-300 text-xs uppercase font-bold tracking-wider">
                       {mode === "view" && filterClass === "all" ? "All Streams" : mode === "view" ? "Invigilators" : "Stream Invigilators"}
                     </Label>
                     {mode === "view" && filterClass !== "all" && (
                       <Badge variant="outline" className="text-xs border-blue-500 text-blue-400">
                         {classes.find(c => c.id === filterClass)?.name || filterClass}
                       </Badge>
                     )}
                   </div>
                   <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
                       {/* In View mode with specific stream: only that stream. Otherwise: all streams */}
                       {(mode === "view" && filterClass !== "all" 
                         ? getStreamsForMergedClass(assignForm.classId).filter(s => s.id === filterClass)
                         : getStreamsForMergedClass(assignForm.classId)
                       ).map(stream => {
                           const streamInvigList = streamInvigilators[stream.id] || [];
                           return (
                           <div key={stream.id} className={`space-y-2 ${mode === "view" && filterClass === "all" ? 'border border-[#1E293B] rounded-lg p-3 bg-[#020817]' : ''}`}>
                               {/* Show stream name in Edit mode OR in View mode when showing All Classes */}
                               {(mode === "edit" || (mode === "view" && filterClass === "all")) && (
                                 <div className="text-sm text-gray-300 font-medium flex items-center gap-2">
                                   <span className="text-blue-400">{stream.name}</span>
                                   {streamInvigList.length > 0 && (
                                     <Badge variant="outline" className="text-xs border-gray-600">
                                       {streamInvigList.length} invigilator{streamInvigList.length > 1 ? 's' : ''}
                                     </Badge>
                                   )}
                                 </div>
                               )}
                               
                               <div className="flex flex-wrap gap-2">
                                 {streamInvigList.length === 0 && (
                                   <span className="text-gray-500 text-sm italic">No invigilators assigned</span>
                                 )}
                                 {streamInvigList.map(tid => {
                                   const t = teachers.find(tr => tr.id === tid);
                                   return (
                                     <Badge key={tid} variant="secondary" className="bg-[#1E293B] text-sm flex items-center gap-1 py-1 px-2">
                                       {t?.name || "Unknown"}
                                       {mode === "edit" && (
                                         <button 
                                           onClick={() => {
                                              const current = streamInvigilators[stream.id] || [];
                                              setStreamInvigilators(prev => ({
                                                ...prev,
                                                [stream.id]: current.filter(id => id !== tid)
                                              }));
                                           }}
                                           className="hover:text-red-400 ml-1"
                                         >×</button>
                                       )}
                                     </Badge>
                                   );
                                 })}
                               </div>

                               {mode === "edit" && (
                               <Select 
                                 value="" 
                                 onValueChange={v => {
                                   // Check if already in THIS stream
                                   const current = streamInvigilators[stream.id] || [];
                                   if (current.includes(v)) {
                                      toast({ title: "Already added to this stream", variant: "destructive" });
                                      return;
                                   }
                                   
                                   // Check if already in ANOTHER stream
                                   const otherStreams = Object.entries(streamInvigilators)
                                     .filter(([sid]) => sid !== stream.id)
                                     .filter(([_, tids]) => tids.includes(v));
                                   
                                   if (otherStreams.length > 0) {
                                     const teacherName = teachers.find(t => t.id === v)?.name || "This teacher";
                                     const otherStreamNames = otherStreams.map(([sid]) => 
                                       classes.find(c => c.id === sid)?.name || sid
                                     ).join(", ");
                                     
                                     const proceed = confirm(`${teacherName} is already assigned to: ${otherStreamNames}\n\nAdd anyway?`);
                                     if (!proceed) return;
                                   }
                                   
                                   setStreamInvigilators(prev => ({ ...prev, [stream.id]: [...current, v] }));
                                 }}
                               >
                                 <SelectTrigger className="h-8 text-xs border-[#1E293B] bg-[#020817] text-white">
                                   <SelectValue placeholder="+ Add Invigilator" />
                                 </SelectTrigger>
                                 <SelectContent className="border-[#1E293B] bg-[#020817] max-h-[200px]">
                                   {teachers.map(t => {
                                      const isBusyElsewhere = selectedSlot && isTeacherBusy(t.id, selectedSlot.date, selectedSlot.timeSlot.startTime);
                                      const isInOtherStream = Object.entries(streamInvigilators)
                                        .filter(([sid]) => sid !== stream.id)
                                        .some(([_, tids]) => tids.includes(t.id));
                                      const isInThisStream = (streamInvigilators[stream.id] || []).includes(t.id);
                                      
                                      return (
                                     <SelectItem 
                                       key={t.id} 
                                       value={t.id} 
                                       disabled={isInThisStream}
                                       className={`text-xs text-white hover:bg-[#1E293B] ${isInThisStream ? 'opacity-50' : ''}`}
                                     >
                                       <div className="flex items-center justify-between w-full gap-2">
                                          <span>{t.title} {t.firstName} {t.lastName}</span>
                                          <div className="flex items-center gap-1">
                                            {isInOtherStream && <span title="Already in another stream"><AlertTriangle className="h-3 w-3 text-amber-500" /></span>}
                                            {isBusyElsewhere && <span title="Busy with another exam"><AlertTriangle className="h-3 w-3 text-red-500" /></span>}
                                          </div>
                                       </div>
                                     </SelectItem>
                                   )})}
                                 </SelectContent>
                               </Select>
                               )}
                           </div>
                       )})}
                   </div>
               </div>
            ) : (
                <div className="space-y-2">
                  <Label className="text-gray-300">Invigilators</Label>
                  
                  <div className="flex flex-wrap gap-2 mb-2 min-h-[30px] p-2 border border-[#1E293B] rounded-md bg-[#0F172A]">
                     {assignForm.invigilators.length === 0 && <span className="text-gray-500 text-sm italic">No invigilators assigned</span>}
                     {assignForm.invigilators.map(tid => {
                        const t = teachers.find(tr => tr.id === tid);
                        return (
                          <Badge key={tid} className="bg-[#1E293B] hover:bg-[#2e3e52] text-white flex items-center gap-1">
                             {t?.name}
                             <button
                               onClick={() => setAssignForm(prev => ({...prev, invigilators: prev.invigilators.filter(id => id !== tid)}))}
                               className="ml-1 hover:text-red-400"
                             >×</button>
                          </Badge>
                        );
                     })}
                  </div>

                  <Select value="" onValueChange={v => {
                      if (assignForm.invigilators.includes(v)) {
                          toast({ title: "Already added", variant: "destructive" });
                          return;
                      }
                      setAssignForm(prev => ({ ...prev, invigilators: [...prev.invigilators, v] }));
                  }}>
                    <SelectTrigger className="border-[#1E293B] bg-[#0F172A] text-white">
                      <SelectValue placeholder="Add invigilator..." />
                    </SelectTrigger>
                    <SelectContent className="border-[#1E293B] bg-[#0F172A]">
                      {teachers.map(t => {
                          const isBusy = selectedSlot && isTeacherBusy(t.id, selectedSlot.date, selectedSlot.timeSlot.startTime);
                          return (
                          <SelectItem key={t.id} value={t.id} className="text-white hover:bg-[#1E293B]">
                              <div className="flex items-center justify-between w-full gap-2">
                                  <span>{t.title} {t.firstName} {t.lastName}</span>
                                  {isBusy && <span title="Busy elsewhere"><AlertTriangle className="h-4 w-4 text-amber-500" /></span>}
                              </div>
                          </SelectItem>
                      )})}
                    </SelectContent>
                  </Select>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Start Time</Label>
                <Input 
                  type="time" 
                  value={assignForm.startTime} 
                  onChange={e => setAssignForm({...assignForm, startTime: e.target.value})} 
                  className="border-[#1E293B] bg-[#0F172A] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">End Time</Label>
                <Input 
                  type="time" 
                  value={assignForm.endTime} 
                  onChange={e => setAssignForm({...assignForm, endTime: e.target.value})} 
                  className="border-[#1E293B] bg-[#0F172A] text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Notes</Label>
              <Textarea 
                placeholder="Special instructions..." 
                value={assignForm.notes} 
                onChange={e => setAssignForm({...assignForm, notes: e.target.value})} 
                className="border-[#1E293B] bg-[#0F172A] text-white placeholder:text-gray-500 min-h-[80px]"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="border-[#1E293B] text-gray-300 hover:bg-[#1E293B] hover:text-white">
              {mode === "view" ? "Close" : "Cancel"}
            </Button>
            {mode === "edit" && (
              <Button onClick={handleAssignExam} disabled={loading} className="bg-[#3879E3] hover:bg-[#3879E3]/90 text-white">
                {loading ? "Saving..." : editingExamId ? "Update" : "Schedule"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Name & Details Dialog */}
      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent className="border border-[#1E293B] bg-[#020817] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">Exam Timetable Details</DialogTitle>
            <DialogDescription className="text-gray-400">
              Set the name and term for this timetable.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Exam Name</Label>
              <Input 
                 placeholder="e.g. Mid-Year Assessment" 
                 value={config.examName} 
                 onChange={e => setConfig({...config, examName: e.target.value})} 
                 className="border-[#1E293B] bg-[#0F172A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Term</Label>
              <Select 
                value={config.term} 
                onValueChange={v => setConfig({...config, term: v})}
              >
                <SelectTrigger className="border-[#1E293B] bg-[#0F172A] text-white">
                  <SelectValue placeholder="Select Term" />
                </SelectTrigger>
                <SelectContent className="border-[#1E293B] bg-[#0F172A]">
                  <SelectItem value="Term 1" className="text-white hover:bg-[#1E293B]">Term 1</SelectItem>
                  <SelectItem value="Term 2" className="text-white hover:bg-[#1E293B]">Term 2</SelectItem>
                  <SelectItem value="Term 3" className="text-white hover:bg-[#1E293B]">Term 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Academic Year</Label>
              <Input 
                 value={config.academicYear} 
                 onChange={e => setConfig({...config, academicYear: e.target.value})} 
                 className="border-[#1E293B] bg-[#0F172A] text-white"
              />
            </div>
          </div>
          <DialogFooter>
             <Button variant="outline" onClick={() => setShowNameDialog(false)} className="border-[#1E293B] text-gray-300 hover:bg-[#1E293B] hover:text-white">
               Cancel
             </Button>
             <Button onClick={handleSaveDetails} className="bg-green-600 hover:bg-green-700 text-white ml-2">
               <Save className="h-4 w-4 mr-2" />Save Changes
             </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Conflict Confirmation Dialog */}
      <Dialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
          <DialogContent className="border border-red-900 bg-[#0F172A] text-white max-w-sm">
             <DialogHeader>
                 <DialogTitle className="flex items-center gap-2 text-red-500">
                     <AlertTriangle className="h-5 w-5" />
                     Scheduling Conflict
                 </DialogTitle>
             </DialogHeader>
             <div className="py-4">
                 <p className="text-gray-300 mb-2">The following conflicts were detected:</p>
                 <ul className="list-disc list-inside space-y-1 text-sm text-red-400">
                     {conflictDetails?.details.map((d, i) => (
                         <li key={i}>{d}</li>
                     ))}
                 </ul>
                 <p className="text-gray-400 text-xs mt-4">Do you want to continue anyway?</p>
             </div>
             <DialogFooter className="gap-2">
                 <Button variant="outline" onClick={() => setShowConflictDialog(false)} className="border border-gray-700 hover:bg-gray-800">
                     Cancel
                 </Button>
                 <Button onClick={executeSaveExam} className="bg-red-600 hover:bg-red-700 text-white">
                     Continue Anyway
                 </Button>
             </DialogFooter>
          </DialogContent>
      </Dialog>
      </ErrorBoundary>
    </SidebarProvider>
  );
}